/**
 * Slider builder + SliderInput live handler — verbatim from the v1 port
 * (research/03 §4; REQ-ELEM-9). Fixed colors #2c6ecb/#e0e0e0, JS-painted
 * WebKit gradient, step snapping, effectiveDecimals, aria attributes.
 * SliderInput uses the BUNDLED jQuery (DIV-004; v1 used the theme's copy).
 */
import { appState } from "../state/app-state";
import { SliderPriceUpdation } from "../pricing";
import { escapeHtml, getConditionalDisplay, getToolTipHtml, parsedName , getElementWidthClass, t , dirAttr } from "./base";
import { isRtl } from "../i18n/translate";
import $ from "jquery";

// Number of decimal places in a value (e.g. 0.5 -> 1, 1 -> 0). Used so the
// slider's displayed value always shows at least the step's precision.
function sliderStepDecimals(value: any): number {
  var n = Number(value);
  if (!isFinite(n) || Math.floor(n) === n) return 0;
  var parts = String(n).split(".");
  return parts[1] ? parts[1].length : 0;
}

// Snap a value onto the slider's step grid (measured from min), clamped to
// [min, max] — mirrors what a native range input does, so the value we render
// matches the thumb position the browser will settle on.
function snapSliderValue(value: any, min: any, max: any, step: any): number {
  var v = Number(value);
  var st = Number(step);
  if (!isFinite(st) || st <= 0) st = 1;
  if (v < min) v = min;
  if (v > max) v = max;
  var snapped = min + Math.round((v - min) / st) * st;
  if (snapped > max) snapped = min + Math.floor((max - min) / st) * st;
  if (snapped < min) snapped = min;
  // Strip floating-point drift introduced by the multiplication (e.g. 0.30000004).
  var precision = Math.max(sliderStepDecimals(st), sliderStepDecimals(min));
  return Number(snapped.toFixed(precision));
}

export function buildSlider(element: any): string {
  var minValue = element.MinValue != undefined ? Number(element.MinValue) : 0;
  var maxValue = element.Maxvalue != undefined ? Number(element.Maxvalue) : 100;
  var maxDecimals = element.Maxdecimals != undefined ? Number(element.Maxdecimals) : 0;
  var step =
    element.StepInterval != undefined &&
    element.StepInterval !== null &&
    element.StepInterval !== "" &&
    Number(element.StepInterval) > 0
      ? Number(element.StepInterval)
      : 1;
  // Show at least as many decimals as the step needs, so a stepped value never
  // displays as a rounded-off number that disagrees with the price (CPCHQ #13).
  var effectiveDecimals = Math.max(maxDecimals, sliderStepDecimals(step));
  var unit =
    element.Unit != undefined && element.Unit !== null ? String(element.Unit) : "";
  var defaultValue =
    element.DefaultValue != undefined &&
    element.DefaultValue !== null &&
    element.DefaultValue !== ""
      ? Number(element.DefaultValue)
      : minValue;
  // Clamp + snap the default onto the step grid so the rendered value, the thumb
  // position and the initial price all agree on load.
  if (defaultValue < minValue) defaultValue = minValue;
  if (defaultValue > maxValue) defaultValue = maxValue;
  defaultValue = snapSliderValue(defaultValue, minValue, maxValue, step);

  var conditionalDisplay = getConditionalDisplay(element);
  var displayValue = Number(defaultValue).toFixed(effectiveDecimals);
  // Fixed slider colors: blue active track + blue thumb, grey inactive track
  // (CPCHQ #13). Intentionally not the brand price color.
  var fillColor = "#2c6ecb";
  var dotColor = "#2c6ecb";
  var pct =
    maxValue > minValue
      ? ((defaultValue - minValue) / (maxValue - minValue)) * 100
      : 0;

  let toolTipHtml = getToolTipHtml(element, "span");
  let unitHtml = unit
    ? " <span class='cpc-slider-unit'>" + escapeHtml(unit) + "</span>"
    : "";

  return (
    "<div class='element cpc-slider-element" + getElementWidthClass(element) + "' data-element='" +
    element.Identifier +
    "' " +
    conditionalDisplay.conditionalTemplate +
    "><label class='cpc-slider-label' style='color:" +
    appState.calculatorStyling.upperTextColor +
    "'><span class='cpc-slider-name'" + dirAttr(element.Name) + ">" +
    t(element.Name) +
    toolTipHtml +
    "</span><span class='cpc-slider-value'> : " +
    "<span class='cpc-slider-value-number'>" + escapeHtml(displayValue) + "</span>" +
    unitHtml +
    "</span></label><input type='range' oninput='SliderInput(this)' onchange='SliderInput(this)' data-identifier='" +
    element.Identifier +
    "' class='calc-prop cpc-slider' min='" +
    minValue +
    "' max='" +
    maxValue +
    "' step='" +
    step +
    "' value='" +
    defaultValue +
    "' data-calcname='" +
    element.Name +
    "' data-maxdecimals='" +
    effectiveDecimals +
    "' data-unit='" +
    escapeHtml(unit) +
    "' name=\"properties[" +
    parsedName(element.Name) +
    "]\" aria-label='" +
    escapeHtml(t(element.Name)) +
    "' aria-valuetext='" +
    escapeHtml(displayValue + (unit ? " " + unit : "")) +
    "' style='--cpc-slider-color:" +
    fillColor +
    ";--cpc-slider-dot:" +
    dotColor +
    ";background:linear-gradient(to " + (isRtl() ? "left" : "right") + "," +
    fillColor +
    " 0%," +
    fillColor +
    " " +
    pct +
    "%,#e0e0e0 " +
    pct +
    "%,#e0e0e0 100%);'/></div>"
  );
}

// Live handler for slider drag/keyboard input. Updates the inline value label and
// the WebKit fill immediately (cheap, every event); price recalc is debounced ~50ms.
export function SliderInput(element: any): void {
  var $el = $(element);
  var min = parseFloat(element.min);
  var max = parseFloat(element.max);
  var maxDecimals = parseInt($el.data("maxdecimals"), 10);
  if (isNaN(maxDecimals)) maxDecimals = 0;
  var raw = parseFloat(element.value);
  if (isNaN(raw)) raw = isNaN(min) ? 0 : min;
  var display = Number(raw).toFixed(maxDecimals);
  var unit = $el.data("unit");

  // Inline "{Label} : {value} {unit}" readout — updates on every event.
  $el
    .closest(".cpc-slider-element")
    .find(".cpc-slider-value-number")
    .text(display);

  // Screen-reader readout including the unit.
  element.setAttribute("aria-valuetext", display + (unit ? " " + unit : ""));

  // Active-track fill (WebKit): repaint the background gradient to the new value.
  var pct = max > min ? ((raw - min) / (max - min)) * 100 : 0;
  var fill = element.style.getPropertyValue("--cpc-slider-color") || "#2c6ecb";
  element.style.background =
    "linear-gradient(to " + (isRtl() ? "left" : "right") + "," +
    fill +
    " 0%," +
    fill +
    " " +
    pct +
    "%,#e0e0e0 " +
    pct +
    "%,#e0e0e0 100%)";

  // Debounced price recalculation (raw value flows into the formula via .calc-prop).
  SliderPriceUpdation(element);
}
